import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import type { Tweens } from '../animation/Tweens';
import { Ease } from '../animation/Ease';
import { motion } from '../animation/Motion';

const BOIL_FPS = 7;

export type Pose = 'attack' | 'hit' | 'ko';

/** What a fighter needs from its art (see entities/art.ts). */
export interface FighterArt {
  frames: Texture[];
  poses: Partial<Record<Pose, Texture[]>>;
  /** Pivot per state, normalised; missing states use idle's. */
  anchors: Partial<Record<Pose | 'idle', [number, number]>>;
  /** Direction the art itself faces. */
  facing: 1 | -1;
}

/**
 * An animated doodle character. Hand-drawn feel comes from:
 *  - frame-based line boil (cycling re-traced frames),
 *  - subtle idle breathing,
 *  - squash & stretch on attacks and hits.
 * All motion is driven by tweens on the plain `pose` object and applied in
 * update(), so nothing is allocated per frame.
 */
export class FighterView extends Container {
  readonly body = new Container();
  private readonly sprite: Sprite;
  private readonly shadow = new Graphics();
  private frames: Texture[];
  private idleFrames: Texture[];
  private poses: Partial<Record<Pose, Texture[]>> = {};
  private anchors: FighterArt['anchors'] = {};
  private current: Pose | 'idle' = 'idle';
  private restingPose: Pose | 'idle' = 'idle';
  private frameIndex = 0;
  private frameClock = 0;
  private t = Math.random() * 10;
  private baseScale = 1;
  readonly pose = { x: 0, y: 0, sx: 1, sy: 1, rot: 0, alpha: 1 };
  private flashMs = 0;
  fainted = false;

  /**
   * @param facing which way this fighter must look (1 = right, towards an
   *   opponent on the right). The sprite is mirrored only when the art faces
   *   the other way.
   */
  constructor(
    art: FighterArt,
    private readonly facing: 1 | -1,
    private readonly tweens: Tweens,
  ) {
    super();
    this.frames = art.frames;
    this.idleFrames = art.frames;
    this.sprite = new Sprite(art.frames[0]);
    for (let i=4; i>0; i--) this.shadow.ellipse(0, 1, 58+i*6, 6+i*2).fill({ color: 0x493622, alpha: 0.025 });
    this.sprite.tint = 0xfff7e9;
    this.addChild(this.shadow, this.body);
    this.body.addChild(this.sprite);
    this.setArt(art);
  }

  /** Swap art (e.g. after the player redraws, or an uploaded drawing arrives). */
  setArt(art: FighterArt): void {
    this.idleFrames = art.frames;
    this.poses = art.poses;
    this.anchors = art.anchors;
    this.sprite.scale.x = this.facing * art.facing;
    this.frames = [];
    const p = this.current;
    this.current = 'idle';
    this.pose_(p === 'ko' && this.fainted ? 'ko' : 'idle');
  }

  private pose_(p: Pose | 'idle'): void {
    if (this.destroyed) return;
    const frames = p === 'idle' ? this.idleFrames : (this.poses[p] ?? this.idleFrames);
    if (frames === this.frames) return;
    this.current = p;
    this.frames = frames;
    this.frameIndex = 0;
    this.sprite.texture = frames[0]!;
    const a = (frames === this.idleFrames ? this.anchors.idle : this.anchors[p as Pose]) ?? this.anchors.idle ?? [0.5, 0.96];
    this.sprite.anchor.set(a[0], a[1]);
  }

  /** Menu action pose uses the pack's own wide frame and pivot. */
  setRestingPose(pose: Pose | 'idle'): void {
    this.restingPose = pose;
    this.pose_(pose);
  }

  /** Fit the doodle to a target height in CSS pixels (idle frame height). */
  setHeight(px: number): void {
    const texH = this.idleFrames[0]?.height ?? 256;
    this.baseScale = px / texH;
    this.shadow.scale.set(this.baseScale * 1.1, this.baseScale);
  }

  update(dt: number): void {
    this.t += dt / 1000;
    if (!motion.reduced && this.frames.length > 1) {
      this.frameClock += dt;
      if (this.frameClock >= 1000 / BOIL_FPS) {
        this.frameClock = 0;
        this.frameIndex = (this.frameIndex + 1) % this.frames.length;
        this.sprite.texture = this.frames[this.frameIndex]!;
      }
    }
    const breath = this.fainted || motion.reduced ? 0 : Math.sin(this.t * 2.4) * 0.018;
    const p = this.pose;
    const s = this.baseScale;
    this.body.position.set(p.x, p.y);
    this.body.scale.set(s * p.sx * (1 - breath * 0.5), s * p.sy * (1 + breath));
    this.body.rotation = p.rot;
    this.body.alpha = p.alpha;
    if (this.flashMs > 0) {
      this.flashMs -= dt;
      this.sprite.tint = this.flashMs > 0 ? 0xff8a8a : 0xfff7e9;
    }
  }

  /** Lunge toward the opponent with anticipation + stretch, then return. */
  async attack(distance: number): Promise<void> {
    const tw = this.tweens;
    const dir = this.facing;
    if (motion.reduced) {
      await tw.to(this.pose, { x: dir * 12 }, 90);
      if (this.destroyed) return;
      await tw.to(this.pose, { x: 0 }, 120);
      return;
    }
    await tw.to(this.pose, { x: -dir * 18, sx: 1.12, sy: 0.88, rot: -dir * 0.08 }, 140, { ease: Ease.outQuad });
    if (this.destroyed) return;
    this.pose_('attack');
    await tw.to(this.pose, { x: dir * distance, sx: 0.86, sy: 1.14, rot: dir * 0.12 }, 120, { ease: Ease.inQuad });
    if (this.destroyed) return;
    void tw.to(this.pose, { sx: 1.15, sy: 0.85 }, 70);
    await tw.wait(70);
    if (this.destroyed) return;
    await tw.to(this.pose, { x: 0, sx: 1, sy: 1, rot: 0 }, 260, { ease: Ease.outBack });
    this.pose_(this.restingPose);
  }

  async hit(heavy: boolean): Promise<void> {
    const tw = this.tweens;
    this.flashMs = 160;
    this.pose_('hit');
    if (motion.reduced) {
      await tw.wait(160);
      this.pose_(this.restingPose);
      return;
    }
    const k = heavy ? 1.6 : 1;
    await tw.to(this.pose, { x: -this.facing * 22 * k, sx: 0.84, sy: 1.12, rot: -this.facing * 0.18 * k }, 90, { ease: Ease.outQuad });
    if (this.destroyed) return;
    await tw.to(this.pose, { x: -this.facing * 8, sx: 1.08, sy: 0.94, rot: this.facing * 0.05 }, 110);
    if (this.destroyed) return;
    await tw.to(this.pose, { x: 0, sx: 1, sy: 1, rot: 0 }, 220, { ease: Ease.outElastic });
    if (!this.fainted) this.pose_(this.restingPose);
  }

  async dodge(): Promise<void> {
    await this.tweens.to(this.pose, { y: -46, x: -this.facing * 30, rot: -this.facing * 0.3 }, 160, { ease: Ease.outQuad, decorative: true });
    if (this.destroyed) return;
    await this.tweens.to(this.pose, { y: 0, x: 0, rot: 0 }, 240, { ease: Ease.outBack, decorative: true });
  }

  async cheer(): Promise<void> {
    for (let i = 0; i < 2; i++) {
      await this.tweens.to(this.pose, { y: -36, sx: 0.92, sy: 1.1 }, 160, { ease: Ease.outQuad, decorative: true });
      if (this.destroyed) return;
      await this.tweens.to(this.pose, { y: 0, sx: 1.1, sy: 0.9 }, 140, { ease: Ease.inQuad, decorative: true });
      if (this.destroyed) return;
    }
    await this.tweens.to(this.pose, { sx: 1, sy: 1 }, 120);
  }

  async faint(): Promise<void> {
    this.fainted = true;
    if (this.poses.ko) {
      // A drawn knocked-out pose: no need to tip the sprite over.
      this.pose_('ko');
      await this.tweens.to(this.pose, { y: 4, alpha: 0.75 }, 380, { ease: Ease.outQuad });
      return;
    }
    await this.tweens.to(this.pose, { rot: -this.facing * 1.45, y: 6, sx: 1.05, sy: 0.95, alpha: 0.55 }, 520, { ease: Ease.outQuad });
  }

  reset(): void {
    this.fainted = false;
    this.pose_('idle');
    this.tweens.kill(this.pose);
    Object.assign(this.pose, { x: 0, y: 0, sx: 1, sy: 1, rot: 0, alpha: 1 });
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.tweens.kill(this.pose);
    super.destroy(options);
  }

  /** Doodle being sketched in: grows from a scribble. */
  async reveal(): Promise<void> {
    this.pose.sx = 0.2;
    this.pose.sy = 0.2;
    this.pose.alpha = 0;
    await this.tweens.to(this.pose, { sx: 1, sy: 1, alpha: 1 }, 520, { ease: Ease.outBack, decorative: true });
    // Finish at full opacity even if the last rendered step was an overshoot.
    if (!this.destroyed) this.pose.alpha = 1;
  }
}
